import { Link } from '@tanstack/react-router';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils/cn';
import type { AppHeaderProps, BreadcrumbTrailItem } from './types';

interface RowProps {
  item: BreadcrumbTrailItem;
  isLast: boolean;
  className?: string;
}

function Crumb({ item, isLast, className }: RowProps) {
  if (isLast) {
    return (
      <span className={cn('text-foreground font-semibold truncate', className)}>
        {item.label}
      </span>
    );
  }
  if (item.href) {
    return (
      <Link
        to={item.href as never}
        className={cn('hover:text-foreground truncate', className)}
      >
        {item.label}
      </Link>
    );
  }
  return <span className={cn('truncate', className)}>{item.label}</span>;
}

export function Breadcrumbs({ breadcrumb, breadcrumbTrail }: AppHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const items: BreadcrumbTrailItem[] = breadcrumbTrail?.length
    ? breadcrumbTrail
    : breadcrumb
      ? [{ label: breadcrumb }]
      : [];

  if (!items.length) return null;

  const first = items[0]!;
  const last = items[items.length - 1]!;
  const middle = items.length > 2 ? items.slice(1, -1) : [];

  return (
    <div className='flex items-center gap-2 ml-1 text-sm text-muted-foreground min-w-0 flex-1'>
      {/* md+ : render every crumb inline */}
      <div className='hidden md:flex items-center gap-2 min-w-0'>
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <span key={i} className='flex items-center gap-2 min-w-0'>
              {i > 0 && <span className='opacity-60 shrink-0'>/</span>}
              <Crumb
                item={item}
                isLast={isLast}
                className={isLast ? 'max-w-xs' : 'max-w-[10rem]'}
              />
            </span>
          );
        })}
      </div>

      {/* < md : first + (overflow menu) + last */}
      <div className='flex md:hidden items-center gap-2 min-w-0'>
        {items.length === 1 ? (
          <Crumb item={last} isLast className='max-w-[14rem]' />
        ) : (
          <>
            <Crumb item={first} isLast={false} className='max-w-[6rem]' />
            <span className='opacity-60 shrink-0'>/</span>
            {middle.length > 0 && (
              <>
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuTrigger className='inline-flex items-center justify-center size-6 rounded hover:bg-muted shrink-0'>
                    <MoreHorizontal size={14} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='start'>
                    {middle.map((item, i) => (
                      <DropdownMenuItem key={i}>
                        {item.href ? (
                          <Link to={item.href as never}>{item.label}</Link>
                        ) : (
                          <span>{item.label}</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className='opacity-60 shrink-0'>/</span>
              </>
            )}
            <Crumb item={last} isLast className='max-w-[10rem]' />
          </>
        )}
      </div>
    </div>
  );
}
